import React, { useState, useEffect } from 'react';
import { Bell, BellOff, CheckCircle, XCircle, Send } from 'lucide-react';

const NotificationSettings = ({ portal = 'employee' }) => {
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadPreferences();
    checkSubscriptionStatus();
  }, [portal]);

  const loadPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreferences(data.preferences);
        
        // Check subscription status based on portal
        if (portal === 'employee') {
          setIsSubscribed(data.employee_portal_subscribed);
        } else {
          setIsSubscribed(data.admin_portal_subscribed);
        }
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkSubscriptionStatus = async () => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (error) {
        console.error('Error checking subscription:', error);
      }
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribeToNotifications = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setMessage({ type: 'error', text: 'Push notifications are not supported in your browser' });
      return;
    }

    try {
      setSaving(true);

      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setMessage({ type: 'error', text: 'Notification permission denied' });
        setSaving(false);
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Get VAPID public key
      const token = localStorage.getItem('token');
      const keyResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/vapid-public-key`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const { publicKey } = await keyResponse.json();

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to server
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          portal: portal
        })
      });

      setIsSubscribed(true);
      setMessage({ type: 'success', text: 'Notifications enabled successfully!' });
      
      // Update preferences to enable
      if (portal === 'employee') {
        await updatePreferences({ ...preferences, employee_portal_enabled: true });
      } else {
        await updatePreferences({ ...preferences, admin_portal_enabled: true });
      }

    } catch (error) {
      console.error('Error subscribing to notifications:', error);
      setMessage({ type: 'error', text: 'Failed to enable notifications' });
    } finally {
      setSaving(false);
    }
  };

  const unsubscribeFromNotifications = async () => {
    try {
      setSaving(true);

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();

        // Remove from server
        const token = localStorage.getItem('token');
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/unsubscribe`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            portal: portal,
            endpoint: subscription.endpoint
          })
        });
      }

      setIsSubscribed(false);
      setMessage({ type: 'success', text: 'Notifications disabled' });
      
      // Update preferences to disable
      if (portal === 'employee') {
        await updatePreferences({ ...preferences, employee_portal_enabled: false });
      } else {
        await updatePreferences({ ...preferences, admin_portal_enabled: false });
      }

    } catch (error) {
      console.error('Error unsubscribing:', error);
      setMessage({ type: 'error', text: 'Failed to disable notifications' });
    } finally {
      setSaving(false);
    }
  };

  const updatePreferences = async (newPreferences) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newPreferences)
      });
      setPreferences(newPreferences);
    } catch (error) {
      console.error('Error updating preferences:', error);
    }
  };

  const sendTestNotification = async () => {
    try {
      setTestSending(true);
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ portal: portal })
      });
      setMessage({ type: 'success', text: 'Test notification sent! Check your notifications.' });
    } catch (error) {
      console.error('Error sending test:', error);
      setMessage({ type: 'error', text: 'Failed to send test notification' });
    } finally {
      setTestSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-gray-500">Loading settings...</div>
      </div>
    );
  }

  const portalName = portal === 'employee' ? 'Employee Portal' : 'Manager Portal';

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Notification Settings</h1>
        <p className="text-sm text-gray-500 mt-1">{portalName}</p>
      </div>

      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Enable/Disable Notifications */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            {isSubscribed ? (
              <div className="p-3 bg-green-100 rounded-lg">
                <Bell className="w-6 h-6 text-green-600" />
              </div>
            ) : (
              <div className="p-3 bg-gray-100 rounded-lg">
                <BellOff className="w-6 h-6 text-gray-600" />
              </div>
            )}
            <div>
              <h3 className="font-semibold text-gray-900">
                Push Notifications {isSubscribed ? 'Enabled' : 'Disabled'}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {isSubscribed
                  ? 'You will receive notifications even when the app is closed'
                  : 'Enable to receive real-time notifications on this device'}
              </p>
            </div>
          </div>
          
          {isSubscribed ? (
            <button
              onClick={unsubscribeFromNotifications}
              disabled={saving}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {saving ? 'Disabling...' : 'Disable'}
            </button>
          ) : (
            <button
              onClick={subscribeToNotifications}
              disabled={saving}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {saving ? 'Enabling...' : 'Enable'}
            </button>
          )}
        </div>

        {isSubscribed && (
          <div className="mt-4 pt-4 border-t">
            <button
              onClick={sendTestNotification}
              disabled={testSending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {testSending ? 'Sending...' : 'Send Test Notification'}
            </button>
          </div>
        )}
      </div>

      {/* Notification Types Info */}
      {isSubscribed && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="font-semibold text-gray-900 mb-4">You'll be notified about:</h3>
          <ul className="space-y-3">
            {portal === 'employee' ? (
              <>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-teal-600 rounded-full mt-2"></div>
                  <div>
                    <div className="font-medium text-gray-900">Time-Off Updates</div>
                    <div className="text-sm text-gray-600">When your requests are approved or denied</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-teal-600 rounded-full mt-2"></div>
                  <div>
                    <div className="font-medium text-gray-900">Task Assignments</div>
                    <div className="text-sm text-gray-600">When new tasks are assigned to you</div>
                  </div>
                </li>
              </>
            ) : (
              <>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-teal-600 rounded-full mt-2"></div>
                  <div>
                    <div className="font-medium text-gray-900">Time-Off Requests</div>
                    <div className="text-sm text-gray-600">When employees request time off</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-teal-600 rounded-full mt-2"></div>
                  <div>
                    <div className="font-medium text-gray-900">Late Clock-Ins (09:15 Mon-Fri)</div>
                    <div className="text-sm text-gray-600">Daily alert if team members haven't clocked in</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-teal-600 rounded-full mt-2"></div>
                  <div>
                    <div className="font-medium text-gray-900">Accident Reports</div>
                    <div className="text-sm text-gray-600">When drivers report accidents</div>
                  </div>
                </li>
              </>
            )}
          </ul>
        </div>
      )}

      {!isSubscribed && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> You need to enable notifications separately for the Employee Portal and Manager Portal.
          </p>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;
